import {
  cloneDeep,
  difference,
  differenceBy,
  get,
  mapValues,
  sum,
  max,
  unionBy
} from 'lodash';
import moment from 'moment';
import { createSelector } from 'reselect';

// constants
import {
  advancedScriptRequirements,
  layerTypes,
  requiredNumImages,
  requiredNumSupportedCropImages,
  supportedCropIds,
  AGRONOMIC_SEASONS
} from 'constants/advanced';
import { resourceType } from 'constants/loading';
import { LATAM_COUNTRIES } from 'constants/countryCodes';

// util
import {
  convertForLocale,
  convertForLocaleScalar
} from '../util/unitConversion';

// selectors
import { isResourceTypeLoaded } from 'loading/selectors';
import { getActiveScenarioUuid } from 'advanced/scenarios/selectors';
import { getPlantingPlanParamsValidationResult, getLayerValidationResultForToggling } from 'validations/selectors';
import { getProductYearFromSeasonCode } from 'scripts/selectors';
import { getCropNameById, getNominalWeightByCropId } from 'crops/selectors';
import { getStepNumber } from 'script-flow/selectors';
import { getActiveFieldCountryCode } from 'current/selectors';

export const canGoBack = () => true; // always will be true, just depends on what the destination is...
export const getStep = (state) => get(state, 'advanced.step'); // TODO: CFVWEB-3365 Update step to new Script Flow

export const isReviewYieldMapsStep = (state) => getStep(state) === 1;
export const isPlantingPlanStep = (state) => getStep(state) === 2;
export const isReviewScenariosStep = (state) => getStep(state) === 3;
export const isFinalStep = (state) => isReviewScenariosStep(state);

export const plantingPlanParamsAreValid = (state) => {
  return !get(getPlantingPlanParamsValidationResult(state), 'isInvalid', false);
};

export const getRecommendedNumZones = (state) =>
  state.advanced.recommendedNumZones;

export const _getAvailablePlantingLayers = (state) =>
  state.advanced.availability.plantingLayers;
export const getAvailableYieldLayers = (state) =>
  state.advanced.availability.yieldLayers;

export const _determinePlantingCropIdAndDateForSeason = ({
  plantingLayers,
  yieldLayers,
  season
}) => {
  const seasonYear = getProductYearFromSeasonCode(season);
  const combinedPlantingAndYieldLayers = unionBy(
    plantingLayers,
    yieldLayers,
    'season'
  );
  const matchingLayer = combinedPlantingAndYieldLayers.find((layer) => {
    return getProductYearFromSeasonCode(layer.season) === seasonYear;
  });
  return matchingLayer
    ? {
      cropId: matchingLayer.cropId,
      date: matchingLayer.date || moment().format('YYYY-MM-DD')
    }
    : {
      date: moment().format('YYYY-MM-DD')
    };
};

export const getAvailableCciLayers = (state) =>
  get(state, 'advanced.availability.cciLayers');

export const getAvailableCciLayersWithCrops = (state) => {
  const cciLayers = cloneDeep(getAvailableCciLayers(state));
  const plantingLayers = _getAvailablePlantingLayers(state);
  const yieldLayers = getAvailableYieldLayers(state);
  cciLayers.forEach((cciLayer) => {
    const { cropId, date } = _determinePlantingCropIdAndDateForSeason({
      plantingLayers,
      yieldLayers,
      season: cciLayer.season
    });
    cciLayer.date = date;
    if (cropId) {
      cciLayer.crop = {
        id: cropId,
        name: getCropNameById(state, cropId)
      };
    }
  });
  return cciLayers;
};

export const getAvailableYieldLayersWithCrops = (state) =>
  getAvailableYieldLayers(state).map((yieldLayer) => {
    if (yieldLayer.cropId) {
      return {
        ...yieldLayer,
        crop: {
          cropNominalWeight: getNominalWeightByCropId(state, yieldLayer.cropId),
          id: yieldLayer.cropId,
          name: getCropNameById(state, yieldLayer.cropId)
        }
      };
    }
    return yieldLayer;
  });

const _getNumAvailableYieldLayers = (state) =>
  getAvailableYieldLayers(state).length;

const _getNumAvailableYieldLayersForCrop = (state, cropId) =>
  getAvailableYieldLayers(state).reduce(
    (acc, layer) => (layer.cropId === cropId ? acc + 1 : acc),
    0
  );

const _getAverageYieldsForCrop = (state, cropId) =>
  getAvailableYieldLayers(state)
  .filter((layer) => layer.cropId === cropId)
  .map((layer) =>
    layer.averageYield
      ? layer.averageYield.q // Units already set/converted earlier.
      : 0
  ); // An expected case where yield was unavailable.

const _getAdvancedParams = (state) => state.advanced.params;

export const getHarvestHistoryParam = (state) =>
  _getAdvancedParams(state).harvestHistory;

export const getAvailableYieldLayersSummaryForCrop = (state, cropId) => {
  const numYieldMaps = _getNumAvailableYieldLayersForCrop(state, cropId);
  const cropNominalWeight = getNominalWeightByCropId(state, cropId);
  if (!numYieldMaps) {
    const avgVal = Math.round(
      convertForLocaleScalar({
        quantity: 180,
        unit: 'bu/ac',
        unitType: 'area-density.yield',
        cropNominalWeight
      })
    );
    return { numYieldMaps: 0, max: 0, avg: 0, avgValue: avgVal };
  }
  const averageYields = _getAverageYieldsForCrop(state, cropId);
  const maxAverageYield = max(averageYields);
  const avg = Math.round(sum(averageYields) / numYieldMaps);

  const convertedMaxAverageYield = convertForLocale({
    quantity: maxAverageYield,
    unit: 'bu/ac',
    unitType: 'area-density.yield',
    cropNominalWeight
  }).toString({ precision: 0 });
  const convertedAvg = convertForLocale({
    quantity: avg,
    unit: 'bu/ac',
    unitType: 'area-density.yield',
    cropNominalWeight
  }).toString({ precision: 0 });

  const avgVal = Math.round(
    convertForLocaleScalar({
      quantity: avg,
      unit: 'bu/ac',
      unitType: 'area-density.yield',
      cropNominalWeight
    })
  );

  return {
    numYieldMaps,
    max: convertedMaxAverageYield,
    avg: convertedAvg,
    avgValue: avgVal
  };
};

const _getNumAvailableSupportedCropYieldLayers = (state) => {
  return getAvailableYieldLayers(state).reduce((supportedCount, yieldLayer) => {
    const isSupportedCrop = supportedCropIds.includes(yieldLayer.cropId);
    return supportedCount + (isSupportedCrop ? 1 : 0);
  }, 0);
};

/** @return A constant indicating if and how requirements for creating an advanced script are met. */
export const getAdvancedScriptRequirementsMatch = (state) => {
  // Are we still waiting for the data?
  if (
    !isResourceTypeLoaded({
      state,
      type: resourceType.ADVANCED_SCRIPT_AVAILABILITY
    })
  ) {
    return advancedScriptRequirements.PENDING; // wait more
  }

  // Did the back end say we can't meet requirements?
  if (!state.advanced.availability.isEnoughForAdvancedScript) {
    return advancedScriptRequirements.DOES_NOT_MEET;
  } // Yes.

  // Can requirements be met with yield layers only?
  const numYieldLayers = _getNumAvailableYieldLayers(state);
  const numSupportedCropYieldLayers
    = _getNumAvailableSupportedCropYieldLayers(state);
  if (
    numYieldLayers >= requiredNumImages[layerTypes.YIELD]
    && numSupportedCropYieldLayers
      >= requiredNumSupportedCropImages[layerTypes.YIELD]
  ) {
    return advancedScriptRequirements.MEETS_WITH_YIELD_ONLY; // Yes.
  }

  // The only option left is using CCI layer, as server said the data had a way to meet requirements
  return advancedScriptRequirements.MEETS_WITH_CCI;
};

export const isReviewHarvestHistoryStep = (state) => getStepNumber(state) === 1;
export const isEnterPlantingPlanStep = (state) => getStepNumber(state) === 2;
export const isReviewScriptStep = (state) => getStepNumber(state) === 3;

export const getAvailableCciLayersWithHarvestHistoryCrops = (state) => {
  const cciLayers = cloneDeep(getAvailableCciLayers(state));
  const harvestHistory = getHarvestHistoryParam(state);
  cciLayers.forEach((cciLayer) => {
    const season = cciLayer.season;
    const cropId = harvestHistory[season]
      ? harvestHistory[season].cropId
      : null;
    cciLayer.date = harvestHistory[season] ? harvestHistory[season].date : null;
    if (cropId) {
      cciLayer.crop = {
        id: cropId,
        name: getCropNameById(state, cropId)
      };
    }
  });
  return cciLayers;
};

export const getExcludedLayersParam = (state) =>
  get(state, 'advanced.params.excludedLayers');

export const getIncludedLayers = (state) => {
  const excludedLayers = getExcludedLayersParam(state);
  return {
    cci: differenceBy(
      getAvailableCciLayersWithHarvestHistoryCrops(state),
      excludedLayers,
      'season'
    ),
    yield: differenceBy(
      getAvailableYieldLayersWithCrops(state),
      excludedLayers,
      'season'
    )
  };
};

export const getIncludedCciLayers = (state) => {
  const includedLayers = getIncludedLayers(state);
  return get(includedLayers, 'cci', []);
};

export const getIncludedYieldLayers = (state) => {
  const includedLayers = getIncludedLayers(state);
  return get(includedLayers, 'yield', []);
};

const hasEnoughImageData = ({ layerType, images }) =>
  images.length >= requiredNumImages[layerType];

const hasEnoughSupportedCrops = ({ layerType, images }) => {
  return (
    images.filter((image) => {
      return supportedCropIds.includes(image.crop.id);
    }).length >= requiredNumSupportedCropImages[layerType]
  );
};

const layerTypeImagesAreValid = ({ layerType, images }) => {
  return (
    hasEnoughImageData({ layerType, images })
    && hasEnoughSupportedCrops({ layerType, images })
  );
};

// Applies to the case where user specifies their crop history / deselects layers
export const doesHarvestHistorySelectionMeetRequirements = (state) => {
  const harvestHistory = getIncludedLayers(state);
  return Object.entries(harvestHistory)
  .map(([layerType, images]) =>
    layerTypeImagesAreValid({ layerType, images })
  )
  .reduce((result, isLayerTypeValid) => result || isLayerTypeValid, false);
};

const _getSeasonsWithCciButNotYieldAvailability = (state) => {
  const cciSeasons = getAvailableCciLayers(state).map((layer) =>
    getProductYearFromSeasonCode(layer.season)
  );
  const yieldSeasons = getAvailableYieldLayers(state).map((layer) =>
    getProductYearFromSeasonCode(layer.season)
  );
  return difference(cciSeasons, yieldSeasons);
};

const _isHarvestHistorySelectedForAllCciUsedSeasons = (state) => {
  const cciUsedSeasons = _getSeasonsWithCciButNotYieldAvailability(state);
  const harvestHistory = getHarvestHistoryParam(state);
  return !cciUsedSeasons.some((season) => !harvestHistory[season]); // True if user has made a selection for all CCI-used seasons.
};

export const hasDeadZone = (state) =>
  get(state, 'advanced.params.hasDeadZone', false);

export const getSupportedCropLayersForLayers = (state, layers) => {
  return layers.filter((layer) => {
    return supportedCropIds.includes(get(layer, 'crop.id'));
  });
};

export const getIncludedLayersParamForRecommendationsRequest = (state) => {
  const includedLayers = getIncludedLayers(state);
  return mapValues(includedLayers, (layers) =>
    layers.map((layer) => getProductYearFromSeasonCode(layer.season))
  );
};

export const isHarvestHistorySelectedForAllIncludedCciSeasons = (state) => {
  const cciLayers = getIncludedCciLayers(state);
  const harvestHistory = getHarvestHistoryParam(state);
  return !cciLayers.some(
    (layer) =>
      !(harvestHistory[layer.season] && harvestHistory[layer.season].cropId)
  ); // True if user has made a selection for all CCI-used seasons.
};

export const hasMinimumNumSupportedCropCciLayersIncluded = (state) => {
  const validationResult = getLayerValidationResultForToggling(state);
  const numSupportedCropCciLayersValidationResult = get(
    validationResult,
    'cciLayersValidationResult.resultsByKey.numSupportedCropCciLayers'
  );
  return !get(numSupportedCropCciLayersValidationResult, 'isInvalid', false);
};

export const hasMinimumNumSupportedCropYieldLayersIncluded = (state) => {
  const validationResult = getLayerValidationResultForToggling(state);
  const numSupportedCropYieldLayersValidationResult = get(
    validationResult,
    'yieldLayersValidationResult.resultsByKey.numSupportedCropYieldLayers'
  );
  return !get(numSupportedCropYieldLayersValidationResult, 'isInvalid', false);
};

export const hasMinimumNumSupportedCropLayersIncluded = (state) => {
  const cciLayers = getIncludedCciLayers(state);
  const yieldLayers = getIncludedYieldLayers(state);
  if (cciLayers.length && yieldLayers.length) {
    return (
      hasMinimumNumSupportedCropCciLayersIncluded(state)
      || hasMinimumNumSupportedCropYieldLayersIncluded(state)
    );
  } else if (cciLayers.length) {
    return hasMinimumNumSupportedCropCciLayersIncluded(state);
  } else if (yieldLayers.length) {
    return hasMinimumNumSupportedCropYieldLayersIncluded(state);
  }
  // if neither layers has length, then we probably don't have the minimum
  return false;
};

export const hasMinimumNumCciLayersIncluded = (state) => {
  const validationResult = getLayerValidationResultForToggling(state);
  const numCciLayersValidationResult = get(
    validationResult,
    'cciLayersValidationResult.resultsByKey.numCciLayers'
  );
  return !get(numCciLayersValidationResult, 'isInvalid', false);
};

export const hasMinimumNumYieldLayersIncluded = (state) => {
  const validationResult = getLayerValidationResultForToggling(state);
  const numYieldLayersValidationResult = get(
    validationResult,
    'yieldLayersValidationResult.resultsByKey.numYieldLayers'
  );
  return !get(numYieldLayersValidationResult, 'isInvalid', false);
};

export const hasMinimumNumLayersIncluded = (state) => {
  const cciLayers = getIncludedCciLayers(state);
  const yieldLayers = getIncludedYieldLayers(state);
  if (cciLayers.length && yieldLayers.length) {
    return (
      hasMinimumNumCciLayersIncluded(state)
      || hasMinimumNumYieldLayersIncluded(state)
    );
  } else if (cciLayers.length) {
    return hasMinimumNumCciLayersIncluded(state);
  } else if (yieldLayers.length) {
    return hasMinimumNumYieldLayersIncluded(state);
  }
  // if neither layers has length, then we probably don't have the minimum
  return false;
};

export const canGoForward = (state) => {
  // Do we have availability Data?
  const availabilityReady = isResourceTypeLoaded({
    state,
    type: resourceType.ADVANCED_SCRIPT_AVAILABILITY
  });
  if (!availabilityReady) {
    return false; // Can't go forward until we have data to inspect
  }

  if (isReviewHarvestHistoryStep(state)) {
    const conditionsMet
      = isHarvestHistorySelectedForAllIncludedCciSeasons(state);
    const advancedScriptRequirementsMatch
      = getAdvancedScriptRequirementsMatch(state);
    // This will be true when user sees "More Data Needed" screen, but even if not coupled to that screen, it's
    // a legit general condition to satisfy.
    return advancedScriptRequirementsMatch
      === advancedScriptRequirements.DOES_NOT_MEET
      ? false
      : conditionsMet;
  }
  if (isEnterPlantingPlanStep(state)) {
    return plantingPlanParamsAreValid(state);
  }
  if (isReviewScriptStep(state)) {
    return !!getActiveScenarioUuid(state) && plantingPlanParamsAreValid(state);
  }
  return false;
};

export const getAgronomicSeasonCodeForCountryCode = (countryCode) => {
  if (countryCode === LATAM_COUNTRIES.ARGENTINA) {
    return AGRONOMIC_SEASONS.SUM;
  }
  if (countryCode !== LATAM_COUNTRIES.BRAZIL) {
    return AGRONOMIC_SEASONS.SPR;
  }
  return '';
};

export const getDefaultAgronomicSeason = createSelector(
  (state) => getActiveFieldCountryCode(state),
  getAgronomicSeasonCodeForCountryCode
);

export const unitTestPrivate = {
  _determinePlantingCropIdAndDateForSeason,
  _getAvailablePlantingLayers,
  _getNumAvailableYieldLayers,
  _getNumAvailableSupportedCropYieldLayers,
  _getSeasonsWithCciButNotYieldAvailability,
  _isHarvestHistorySelectedForAllCciUsedSeasons
};
